// src/components/RoleCreationModal.jsx (or wherever yours lives)
import React, { useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Alert,
} from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import "./RoleCreationModal.css";
import notify from "../utils/notify";

const { Text } = Typography;

/**
 * AntD v6 upgrades applied:
 * - Modal: `open` replaces `visible`
 * - Modal: `destroyOnClose` replaced by `destroyOnHidden`
 * - message: use App.useApp() instead of importing `message` from antd
 *
 * Backwards-compat props supported:
 * - accepts `visible` (old) and `open` (new)
 * - accepts `onCancel` or `handleClose`
 */
const RoleCreationModal = ({
  open,
  visible, // legacy
  onCancel,
  handleClose, // legacy alias used in your ModuleAssignment
  onRoleCreated,
  hotelId: hotelIdProp, // optional override
}) => {
  const { authData } = useAuth();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const actualOpen = typeof open === "boolean" ? open : !!visible;
  const close = onCancel || handleClose || (() => {});

  const hotelId = useMemo(
    () => hotelIdProp ?? authData?.hotelId,
    [hotelIdProp, authData?.hotelId]
  );

  const handleSubmit = async () => {
    if (!hotelId) {
      notify.warning("No hotel selected for this account.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const values = await form.validateFields();
      const roleName = (values?.roleName || "").trim();

      const response = await axiosInstance.post("/Roles", {
        RoleName: roleName,
        HotelId: hotelId,
      });

      notify.success("Role created successfully");
      form.resetFields();

      // Pass the created role back to parent
      onRoleCreated?.(response.data);

      // Auto-close after success (common UX)
      close?.();
    } catch (err) {
      console.error("Error creating role:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create role. Please try again.";
      setError(msg);

      notify.error({
        message: "Role Creation Failed",
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SafetyCertificateOutlined />
          <Text strong>Create New Role</Text>
        </Space>
      }
      open={actualOpen}
      onCancel={close}
      footer={null}
      width={600}
      destroyOnHidden
      className="role-creation-modal"
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Role Name"
          name="roleName"
          normalize={(v) => (typeof v === "string" ? v : "")}
          rules={[
            { required: true, message: "Please enter role name" },
            { min: 3, message: "Minimum 3 characters" },
            { max: 50, message: "Maximum 50 characters" },
            {
              validator: (_, value) => {
                const v = (value || "").trim();
                if (!v) return Promise.resolve();
                if (v.length < 3) return Promise.reject(new Error("Minimum 3 characters"));
                if (v.length > 50) return Promise.reject(new Error("Maximum 50 characters"));
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="E.g. Manager, Receptionist" autoFocus maxLength={50} />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Role
            </Button>
            <Button onClick={close} disabled={loading}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RoleCreationModal;
